import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import Layout from '@/components/Layout';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Case } from '@/types';
import { CasesApi } from '@/api';
import Pagination from '@/components/ui/Pagination';
import { formatDate, formatRelativeTime } from '@/utils';
import {
  RiAddLine as PlusIcon,
  RiFolderLine as FolderIcon,
  RiFileTextLine as DocumentTextIcon,
  RiCalendarLine as CalendarIcon,
  RiTimeLine as ClockIcon,
  RiGridLine as Squares2X2Icon,
  RiLayoutGridLine as TableCellsIcon,
  RiArrowUpLine as ChevronUpIcon,
  RiArrowDownLine as ChevronDownIcon,
  RiEyeLine as EyeIcon,
  RiArrowUpDownLine as ChevronUpDownIcon,
  RiDeleteBin6Line as DeleteIcon,
} from 'react-icons/ri';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
import { usePageTracking } from '@/hooks/usePageTracking';

type ViewMode = 'grid' | 'table';
type SortField =
  | 'name'
  | 'applicantName'
  | 'visaType'
  | 'company'
  | 'updatedAt';
type SortDirection = 'asc' | 'desc';

const CasesPage: React.FC = () => {
  usePageTracking(); // Track page view
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  // Initialize from localStorage synchronously to avoid one render with defaults
  const initialPageSize = (() => {
    if (typeof window === 'undefined') return 20;
    const raw = localStorage.getItem('cases-page-size');
    const n = raw ? parseInt(raw, 10) : 20;
    return !isNaN(n) && n > 0 ? n : 20;
  })();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [deletingCaseId, setDeletingCaseId] = useState<string | null>(null);

  useEffect(() => {
    // Load view mode only (pageSize already set synchronously)
    const savedViewMode = localStorage.getItem('cases-view-mode') as ViewMode;
    if (savedViewMode === 'grid' || savedViewMode === 'table') {
      setViewMode(savedViewMode);
    }
  }, []);

  useEffect(() => {
    loadCases(page, pageSize);
  }, [page, pageSize]);

  useEffect(() => {
    localStorage.setItem('cases-page-size', String(pageSize));
  }, [pageSize]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('cases-view-mode', mode);

    analytics.track(ANALYTICS_EVENTS.CASE_LIST_VIEW_MODE_CHANGED, {
      mode: mode,
    });
  };

  const getSortFieldLabel = (field: SortField) => {
    switch (field) {
      case 'updatedAt':
        return 'Last Modified';
      case 'applicantName':
        return 'Beneficiary';
      case 'visaType':
        return 'Visa Type';
      case 'name':
        return 'Case Name';
      case 'company':
        return 'Petitioner';
      default:
        return String(field).charAt(0).toUpperCase() + String(field).slice(1);
    }
  };

  const SortDropdown = () => (
    <div className="relative">
      <button
        onClick={() => setShowSortDropdown(!showSortDropdown)}
        className="flex items-center space-x-1 px-3 py-2 text-sm text-neutral-600 hover:text-neutral-900 border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
      >
        <span>Sort by {getSortFieldLabel(sortField)}</span>
        <ChevronUpDownIcon className="h-4 w-4" />
      </button>

      {showSortDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowSortDropdown(false)}
          />
          {/* Dropdown */}
          <div className="absolute right-0 mt-1 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg z-20">
            <div className="py-1">
              {(
                [
                  ['applicantName', 'Beneficiary'],
                  ['visaType', 'Visa Type'],
                  ['company', 'Petitioner'],
                  ['updatedAt', 'Last Modified'],
                  ['name', 'Case Name'],
                ] as [SortField, string][]
              ).map(([field, label]) => (
                <button
                  key={field}
                  onClick={() => {
                    handleSort(field);
                    setShowSortDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 flex items-center justify-between ${
                    sortField === field
                      ? 'text-primary-700 bg-primary-50'
                      : 'text-neutral-700'
                  }`}
                >
                  <span>{label}</span>
                  {sortField === field &&
                    (sortDirection === 'asc' ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    ))}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const loadCases = async (p = 1, limit = pageSize) => {
    try {
      setIsLoading(true);
      const response = await CasesApi.getCases(p, limit);
      const casesData = response.data || [];
      setCases(casesData);
      setTotal(response.total);
      setTotalPages(response.totalPages);
      setPage(p);
    } catch (err) {
      setError('Failed to load cases');
      console.error('Load cases error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCase = async (caseId: string, caseName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete case "${caseName}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeletingCaseId(caseId);
      await CasesApi.deleteCase(caseId);

      analytics.track(ANALYTICS_EVENTS.CASE_DELETED, {
        case_id: caseId,
      });

      // Reload cases after deletion
      await loadCases(page, pageSize);
    } catch (err) {
      console.error('Delete case error:', err);
      setError('Failed to delete case');

      analytics.track(ANALYTICS_EVENTS.ERROR_OCCURRED, {
        error_type: 'case_deletion_failed',
        case_id: caseId,
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setDeletingCaseId(null);
    }
  };

  const handleSort = (field: SortField) => {
    let newDirection: SortDirection = 'asc';
    if (sortField === field) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortField(field);
      setSortDirection('asc');
    }

    analytics.track(ANALYTICS_EVENTS.CASE_LIST_SORTED, {
      field: field,
      direction: newDirection,
    });
  };

  const getSortedCases = () => {
    // Filter out deleted cases
    const filtered = cases.filter(c => !c.isDeleted);
    const sorted = [...filtered].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'applicantName':
          aVal = a.applicantName?.toLowerCase() || '';
          bVal = b.applicantName?.toLowerCase() || '';
          break;
        case 'visaType':
          aVal = (a.visaTypeRef?.name || a.visaType)?.toLowerCase() || '';
          bVal = (b.visaTypeRef?.name || b.visaType)?.toLowerCase() || '';
          break;
        case 'company':
          aVal = a.company?.name?.toLowerCase() || '';
          bVal = b.company?.name?.toLowerCase() || '';
          break;
        case 'updatedAt':
          aVal = new Date(a.updatedAt || a.lastActivityAt || a.createdAt);
          bVal = new Date(b.updatedAt || b.lastActivityAt || b.createdAt);
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const TableHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
  }> = ({ field, children }) => (
    <th
      className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 select-none transition-colors"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        handleSort(field);
      }}
    >
      <div className="flex items-center gap-2">
        <span>{children}</span>
        {sortField === field &&
          (sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-4 w-4 text-primary-600" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-primary-600" />
          ))}
      </div>
    </th>
  );

  const TableView = () => (
    <div className="bg-white shadow-soft overflow-hidden rounded-xl border border-neutral-200/80">
      {/* Desktop table */}
      <div className="hidden md:block">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              <TableHeader field="applicantName">Beneficiary</TableHeader>
              <TableHeader field="visaType">Visa Type</TableHeader>
              <TableHeader field="updatedAt">Last Modified</TableHeader>
              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200">
            {getSortedCases().map(caseItem => (
              <tr
                key={caseItem.id}
                onClick={() => navigate(`/cases/${caseItem.id}`)}
                className="hover:bg-neutral-50 transition-all cursor-pointer group"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 group-hover:text-primary-700 transition-colors">
                  {caseItem.applicantName || caseItem.name || (
                    <span className="text-neutral-400 italic">
                      Not specified
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                  {caseItem.visaTypeRef?.name || caseItem.visaType || (
                    <span className="text-neutral-400 italic">
                      Not specified
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                  <div className="flex flex-col">
                    <span>
                      {formatRelativeTime(
                        caseItem.updatedAt ||
                          caseItem.lastActivityAt ||
                          caseItem.createdAt
                      )}
                    </span>
                    <span className="text-xs text-neutral-400">
                      {formatDate(
                        caseItem.updatedAt ||
                          caseItem.lastActivityAt ||
                          caseItem.createdAt
                      )}
                    </span>
                  </div>
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-sm font-medium"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2">
                    <Link to={`/cases/${caseItem.id}`}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex items-center space-x-1 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4" />
                        <span>Open</span>
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={e => {
                        e?.preventDefault();
                        e?.stopPropagation();
                        handleDeleteCase(
                          caseItem.id,
                          caseItem.applicantName || caseItem.name
                        );
                      }}
                      disabled={deletingCaseId === caseItem.id}
                      className="flex items-center space-x-1"
                    >
                      <DeleteIcon className="h-4 w-4" />
                      <span className="hidden xl:inline">
                        {deletingCaseId === caseItem.id
                          ? 'Deleting...'
                          : 'Delete'}
                      </span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden">
        <div className="p-4 bg-neutral-50 border-b border-neutral-200">
          <SortDropdown />
        </div>
        <div className="divide-y divide-neutral-200">
          {getSortedCases().map(caseItem => (
            <div key={caseItem.id} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-neutral-900 text-sm">
                  {caseItem.applicantName || caseItem.name || 'Not specified'}
                </h3>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-500">Visa Type:</span>
                  <span className="text-neutral-900">
                    {caseItem.visaTypeRef?.name ||
                      caseItem.visaType ||
                      'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Last Modified:</span>
                  <span className="text-neutral-900">
                    {formatRelativeTime(
                      caseItem.updatedAt ||
                        caseItem.lastActivityAt ||
                        caseItem.createdAt
                    )}
                  </span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-neutral-100">
                <div className="grid grid-cols-2 gap-2">
                  <Link to={`/cases/${caseItem.id}`} className="flex-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full flex items-center justify-center space-x-1"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>Open</span>
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() =>
                      handleDeleteCase(
                        caseItem.id,
                        caseItem.applicantName || caseItem.name
                      )
                    }
                    disabled={deletingCaseId === caseItem.id}
                    className="flex items-center justify-center space-x-1"
                  >
                    <DeleteIcon className="h-4 w-4" />
                    <span>
                      {deletingCaseId === caseItem.id
                        ? 'Deleting...'
                        : 'Delete'}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cases - ImmStack</title>
      </Helmet>
      <Layout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-neutral-900">Cases</h1>
              <p className="text-neutral-600 mt-1">
                Manage your immigration cases and documents
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {/* View mode toggle */}
              <div className="flex items-center bg-white border border-neutral-300 rounded-lg p-1">
                <button
                  onClick={() => handleViewModeChange('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                  title="Grid view"
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleViewModeChange('table')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'table'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                  title="Table view"
                >
                  <TableCellsIcon className="h-5 w-5" />
                </button>
              </div>

              <Link to="/cases/new">
                <Button className="flex items-center space-x-2">
                  <PlusIcon className="h-4 w-4" />
                  <span>New Case</span>
                </Button>
              </Link>
            </div>
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4">
              <p className="text-sm text-error-700">{error}</p>
            </div>
          )}

          {/* Cases Content */}
          {cases.length > 0 ? (
            <>
              {/* Sort indicator for grid view */}
              {viewMode === 'grid' && (
                <div className="flex items-center justify-between text-sm text-neutral-600 bg-neutral-50 py-2 rounded-lg">
                  <div className="flex items-center ml-auto">
                    <SortDropdown />
                  </div>
                </div>
              )}

              {viewMode === 'table' ? (
                <>
                  <TableView />
                  <Pagination
                    meta={{
                      page,
                      totalPages,
                      total,
                      limit: pageSize,
                      hasNextPage: page < totalPages,
                      hasPrevPage: page > 1,
                    }}
                    onChange={p => setPage(p)}
                    onPageSizeChange={size => {
                      setPageSize(size);
                      setPage(1);
                    }}
                    loading={isLoading}
                  />
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getSortedCases().map(caseItem => (
                    <div
                      key={caseItem.id}
                      onClick={() => navigate(`/cases/${caseItem.id}`)}
                      className="cursor-pointer"
                    >
                      <Card hover className="h-full relative group" shadow="md">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                              <FolderIcon className="h-5 w-5" />
                            </div>
                            <h3 className="font-semibold text-neutral-900 truncate">
                              {caseItem.name}
                            </h3>
                          </div>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteCase(
                                caseItem.id,
                                caseItem.applicantName || caseItem.name
                              );
                            }}
                            disabled={deletingCaseId === caseItem.id}
                            className="ml-2 p-2 rounded-lg text-neutral-400 hover:text-error-600 hover:bg-error-50 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                            title="Delete case"
                          >
                            {deletingCaseId === caseItem.id ? (
                              <svg
                                className="animate-spin h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            ) : (
                              <DeleteIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>

                        {(caseItem.caseDescription || caseItem.description) && (
                          <p className="text-sm text-neutral-600 mb-4 line-clamp-2 leading-relaxed">
                            {caseItem.caseDescription || caseItem.description}
                          </p>
                        )}

                        <div className="space-y-3">
                          {/* Beneficiary */}
                          {caseItem.applicantName && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-neutral-700">Beneficiary:</span>
                              <span className="text-neutral-900">
                                {caseItem.applicantName}
                              </span>
                            </div>
                          )}

                          {/* Visa Type */}
                          {(caseItem.visaTypeRef?.name ||
                            caseItem.visaType) && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-neutral-700">Visa Type:</span>
                              <span className="px-2.5 py-1 bg-primary-50 text-primary-700 rounded-lg text-xs font-medium border border-primary-200/80">
                                {caseItem.visaTypeRef?.name ||
                                  caseItem.visaType}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-4 border-t border-neutral-200/80">
                            <div className="flex items-center gap-4 text-xs text-neutral-500">
                              <div className="flex items-center gap-1.5">
                                <CalendarIcon className="h-3.5 w-3.5" />
                                <span>{formatDate(caseItem.createdAt)}</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 text-xs text-neutral-500">
                              <ClockIcon className="h-3.5 w-3.5" />
                              <span>
                                {formatRelativeTime(
                                  caseItem.updatedAt ||
                                    caseItem.lastActivityAt ||
                                    caseItem.createdAt
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              )}
              {viewMode === 'grid' && (
                <Pagination
                  meta={{
                    page,
                    totalPages,
                    total,
                    limit: pageSize,
                    hasNextPage: page < totalPages,
                    hasPrevPage: page > 1,
                  }}
                  onChange={p => setPage(p)}
                  onPageSizeChange={size => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  loading={isLoading}
                  compact
                />
              )}
            </>
          ) : (
            <Card className="text-center py-12">
              <FolderIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                No cases found
              </h3>
              <p className="text-neutral-600 mb-6">
                {page > 1
                  ? 'You have navigated past the last page. Try going back.'
                  : 'Get started by creating your first case'}
              </p>
              {page === 1 && (
                <Link to="/cases/new">
                  <Button className="inline-flex items-center space-x-2">
                    <PlusIcon className="h-4 w-4" />
                    <span>Create your first case</span>
                  </Button>
                </Link>
              )}
            </Card>
          )}
        </div>
      </Layout>
    </>
  );
};

export default CasesPage;
